import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import AutocompleteSelect from '@/components/ui/AutocompleteSelect';
import { AdCampaignLocationTarget } from '@/types/ads';
import { useAddressAdminAreas, useAddressCountries, useAddressLocalities } from '@/hooks';
import { Plus } from 'lucide-react';

type LocationTargetSelectorProps = {
  value: AdCampaignLocationTarget[];
  onChange: (next: AdCampaignLocationTarget[]) => void;
  disabled?: boolean;
};

const buildLocationKey = (target: AdCampaignLocationTarget) =>
  `${target.countryId || ''}|${target.adminAreaId || ''}|${target.localityId || ''}`;

const LocationTargetSelector = ({ value, onChange, disabled = false }: LocationTargetSelectorProps) => {
  const locationTargets = value ?? [];
  const [countrySearch, setCountrySearch] = useState('');
  const [adminAreaSearch, setAdminAreaSearch] = useState('');
  const [localitySearch, setLocalitySearch] = useState('');
  const [selectedCountryId, setSelectedCountryId] = useState('');
  const [selectedAdminAreaId, setSelectedAdminAreaId] = useState('');
  const [selectedLocalityId, setSelectedLocalityId] = useState('');
  const [countryNameMap, setCountryNameMap] = useState<Record<string, string>>({});
  const [adminAreaNameMap, setAdminAreaNameMap] = useState<Record<string, string>>({});
  const [localityNameMap, setLocalityNameMap] = useState<Record<string, string>>({});

  const { data: countries, isLoading: isCountriesLoading } = useAddressCountries(countrySearch);
  const { data: adminAreas, isLoading: isAdminAreasLoading } = useAddressAdminAreas(selectedCountryId, adminAreaSearch);
  const { data: localities, isLoading: isLocalitiesLoading } = useAddressLocalities(selectedAdminAreaId, localitySearch);

  const countryOptions = (countries ?? []).map((country) => ({
    value: country.id,
    label: country.name,
  }));
  const adminAreaOptions = (adminAreas ?? []).map((area) => ({
    value: area.id,
    label: area.name,
  }));
  const localityOptions = (localities ?? []).map((locality) => ({
    value: locality.id,
    label: locality.name,
  }));

  const selectedCountry = countries?.find((country) => country.id === selectedCountryId);
  const adminAreaLabel = selectedCountry?.adminAreaLabel ?? 'Estado/Provincia';
  const localityLabel = selectedCountry?.localityLabel ?? 'Ciudad/Municipio';

  useEffect(() => {
    if (!countries || countries.length === 0) return;
    setCountryNameMap((prev) => {
      const next = { ...prev };
      countries.forEach((country) => {
        next[country.id] = country.name;
      });
      return next;
    });
  }, [countries]);

  useEffect(() => {
    if (!adminAreas || adminAreas.length === 0) return;
    setAdminAreaNameMap((prev) => {
      const next = { ...prev };
      adminAreas.forEach((area) => {
        next[area.id] = area.name;
      });
      return next;
    });
  }, [adminAreas]);

  useEffect(() => {
    if (!localities || localities.length === 0) return;
    setLocalityNameMap((prev) => {
      const next = { ...prev };
      localities.forEach((locality) => {
        next[locality.id] = locality.name;
      });
      return next;
    });
  }, [localities]);

  useEffect(() => {
    setSelectedAdminAreaId('');
    setSelectedLocalityId('');
    setAdminAreaSearch('');
    setLocalitySearch('');
  }, [selectedCountryId]);

  useEffect(() => {
    setSelectedLocalityId('');
    setLocalitySearch('');
  }, [selectedAdminAreaId]);

  const resolveLocationLabel = (target: AdCampaignLocationTarget) => {
    if (target.label) {
      return target.label;
    }
    const countryName = countryNameMap[target.countryId] ?? target.countryId;
    const adminName = target.adminAreaId ? (adminAreaNameMap[target.adminAreaId] ?? target.adminAreaId) : null;
    const localityName = target.localityId ? (localityNameMap[target.localityId] ?? target.localityId) : null;
    if (localityName) {
      return `${countryName} / ${adminName ?? target.adminAreaId ?? ''} / ${localityName}`;
    }
    if (adminName) {
      return `${countryName} / ${adminName}`;
    }
    return countryName;
  };

  const addLocationTarget = () => {
    if (!selectedCountryId) {
      toast.error('Selecciona un país primero');
      return;
    }
    const nextTarget: AdCampaignLocationTarget = {
      countryId: selectedCountryId,
      adminAreaId: selectedAdminAreaId || undefined,
      localityId: selectedLocalityId || undefined,
    };
    const nextKey = buildLocationKey(nextTarget);
    if (locationTargets.some((target) => buildLocationKey(target) === nextKey)) {
      toast.error('Ubicación ya agregada');
      return;
    }
    onChange([...locationTargets, nextTarget]);
  };

  const clearLocationTargets = () => {
    onChange([]);
  };

  const removeLocationTarget = (index: number) => {
    const next = locationTargets.filter((_, idx) => idx !== index);
    onChange(next);
  };

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Segmentación geográfica</h2>
          <p className="text-xs text-gray-500">
            Selecciona las ubicaciones donde se mostrará la campaña.
          </p>
        </div>
        {locationTargets.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={clearLocationTargets}
            disabled={disabled}
          >
            Quitar todas
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr_1fr_auto] gap-y-4 gap-x-2 items-end">
        <AutocompleteSelect
          label="País"
          placeholder="Busca un país"
          value={selectedCountryId}
          options={countryOptions}
          onChange={(value) => setSelectedCountryId(value)}
          onSearch={(value) => setCountrySearch(value)}
          loading={isCountriesLoading}
          disabled={disabled}
        />

        <AutocompleteSelect
          label={adminAreaLabel}
          placeholder={selectedCountryId ? `Busca ${adminAreaLabel.toLowerCase()}` : 'Selecciona un país primero'}
          value={selectedAdminAreaId}
          options={adminAreaOptions}
          onChange={(value) => setSelectedAdminAreaId(value)}
          onSearch={(value) => setAdminAreaSearch(value)}
          loading={isAdminAreasLoading}
          disabled={disabled || !selectedCountryId}
        />

        <AutocompleteSelect
          label={localityLabel}
          placeholder={selectedAdminAreaId ? `Busca ${localityLabel.toLowerCase()}` : 'Selecciona una provincia/estado primero'}
          value={selectedLocalityId}
          options={localityOptions}
          onChange={(value) => setSelectedLocalityId(value)}
          onSearch={(value) => setLocalitySearch(value)}
          loading={isLocalitiesLoading}
          disabled={disabled || !selectedAdminAreaId}
        />

        <div className="flex md:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={addLocationTarget}
            disabled={disabled || !selectedCountryId}
            className="w-full md:w-auto h-10.5"
          >
            <Plus className="h-4 w-4 mr-1" />
          </Button>
        </div>
      </div>

      {locationTargets.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 p-3 text-xs text-gray-500">
          No hay ubicaciones seleccionadas.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {locationTargets.map((target, index) => (
            <span
              key={`${buildLocationKey(target)}-${index}`}
              className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700"
            >
              {resolveLocationLabel(target)}
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                onClick={() => removeLocationTarget(index)}
                disabled={disabled}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </section>
  );
};

export default LocationTargetSelector;
